import { createClient } from "@/app/utils/supabase/client";
import {
  QualityChecklistQuestion,
  QualityChecklistQuestionInsert,
  QualityChecklistQuestionUpdate,
  QualityChecklistPdfReference,
  QualityChecklistPdfReferenceInsert,
  QualityChecklistPdfReferenceUpdate,
  ProjectDocument
} from "@/app/lib/supabase/types";
import { SUPABASE_TABLES } from "@/app/lib/supabase/supabaseDBConstants";

export const qualityChecklistsApi = {
  // Quality Checklist Questions operations
  async getQuestions(companyId: string): Promise<QualityChecklistQuestion[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKLIST_QUESTIONS)
      .select("*")
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .order("question_text", { ascending: true });

    if (error) {
      console.error("Error fetching quality checklist questions:", error);
      throw error;
    }

    return data || [];
  },

  async getQuestion(questionId: string): Promise<QualityChecklistQuestion> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKLIST_QUESTIONS)
      .select("*")
      .eq("id", questionId)
      .is("deleted_at", null)
      .single();

    if (error) {
      console.error("Error fetching quality checklist question:", error);
      throw error;
    }

    if (!data) {
      throw new Error("Quality checklist question not found");
    }

    return data;
  },

  async createQuestion(question: QualityChecklistQuestionInsert): Promise<QualityChecklistQuestion> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKLIST_QUESTIONS)
      .insert(question)
      .select()
      .single();

    if (error) {
      console.error("Error creating quality checklist question:", error);
      throw error;
    }

    return data;
  },

  async updateQuestion(questionId: string, updates: QualityChecklistQuestionUpdate): Promise<QualityChecklistQuestion> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKLIST_QUESTIONS)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", questionId)
      .select()
      .single();

    if (error) {
      console.error("Error updating quality checklist question:", error);
      throw error;
    }

    return data;
  },

  async deleteQuestion(questionId: string): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKLIST_QUESTIONS)
      .update({
        deleted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", questionId);

    if (error) {
      console.error("Error deleting quality checklist question:", error);
      throw error;
    }
  },

  // Quality Checklist Items operations (per production stage)
  // Note: In the new structure, checklist items are the questions themselves
  async getChecklistQuestionsByProductionStage(productionStageId: string): Promise<QualityChecklistQuestion[]> {
    const supabase = createClient();

    // First get all questions for the production stage
    const { data: allQuestions, error } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKLIST_QUESTIONS)
      .select("*")
      .eq("production_stage_id", productionStageId)
      .is("deleted_at", null)
      .order("display_order", { ascending: true });

    if (error) {
      console.error("Error fetching quality checklist items:", error);
      throw error;
    }

    if (!allQuestions || allQuestions.length === 0) {
      return [];
    }

    // Group by question_uuid and get the latest version of each
    const latestQuestions: QualityChecklistQuestion[] = Object.values(
      allQuestions.reduce((acc, question) => {
        if (!acc[question.question_uuid] ||
          question.version > acc[question.question_uuid].version) {
          acc[question.question_uuid] = question;
        }
        return acc;
      }, {} as Record<string, QualityChecklistQuestion>)
    );

    // Sort by display_order
    return latestQuestions.sort((a, b) => a.display_order - b.display_order);
  },

  // TODO: need to delete below commented
  // async getChecklistItem(itemId: string): Promise<QualityChecklistQuestion> {
  //   const supabase = createClient();

  //   const { data, error } = await supabase
  //     .from(SUPABASE_TABLES.QUALITY_CHECKLIST_QUESTIONS)
  //     .select("*")
  //     .eq("id", itemId)
  //     .is("deleted_at", null)
  //     .single();

  //   if (error) {
  //     console.error("Error fetching quality checklist item:", error);
  //     throw error;
  //   }

  //   if (!data) {
  //     throw new Error("Quality checklist item not found");
  //   }

  //   return data;
  // },

  // async createChecklistItem(item: QualityChecklistItemInsert): Promise<QualityChecklistItem> {
  //   const supabase = createClient();

  //   const { data, error } = await supabase
  //     .from(SUPABASE_TABLES.QUALITY_CHECKLIST_QUESTIONS)
  //     .insert(item)
  //     .select()
  //     .single();

  //   if (error) {
  //     console.error("Error creating quality checklist item:", error);
  //     throw error;
  //   }

  //   return data;
  // },

  // async updateChecklistItem(itemId: string, updates: QualityChecklistItemUpdate): Promise<QualityChecklistItem> {
  //   const supabase = createClient();

  //   const { data, error } = await supabase
  //     .from(SUPABASE_TABLES.QUALITY_CHECKLIST_QUESTIONS)
  //     .update({
  //       ...updates,
  //       updated_at: new Date().toISOString(),
  //     })
  //     .eq("id", itemId)
  //     .select()
  //     .single();

  //   if (error) {
  //     console.error("Error updating quality checklist item:", error);
  //     throw error;
  //   }

  //   return data;
  // },

  // async deleteChecklistItem(itemId: string): Promise<void> {
  //   const supabase = createClient();

  //   const { error } = await supabase
  //     .from(SUPABASE_TABLES.QUALITY_CHECKLIST_QUESTIONS)
  //     .update({
  //       deleted_at: new Date().toISOString(),
  //       updated_at: new Date().toISOString(),
  //     })
  //     .eq("id", itemId);

  //   if (error) {
  //     console.error("Error deleting quality checklist item:", error);
  //     throw error;
  //   }
  // },

  async reorderChecklistQuestions(productionStageId: string, questionOrders: { id: string; display_order: number }[]): Promise<void> {
    const supabase = createClient();

    // Update each item's display order
    const updates = questionOrders.map(({ id, display_order }) =>
      supabase
        .from(SUPABASE_TABLES.QUALITY_CHECKLIST_QUESTIONS)
        .update({
          display_order,
          updated_at: new Date().toISOString()
        })
        .eq("id", id)
        .eq("production_stage_id", productionStageId)
    );

    const results = await Promise.all(updates);

    const errors = results.filter(result => result.error);
    if (errors.length > 0) {
      console.error("Errors reordering checklist items:", errors);
      throw new Error("Failed to reorder some checklist items");
    }
  },

  // PDF Reference operations
  async getPdfReferencesByChecklistQuestion(checklistQuestionId: string): Promise<QualityChecklistPdfReference[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKLIST_PDF_REFERENCES)
      .select("*")
      .eq("question_id", checklistQuestionId)
      .order("page_number", { ascending: true });

    if (error) {
      console.error("Error fetching PDF references:", error);
      throw error;
    }

    return data || [];
  },

  async getPdfReferencesByProductionStage(productionStageId: string): Promise<QualityChecklistPdfReference[]> {
    const supabase = createClient();

    // Get all questions for the production stage
    const { data: questions, error: questionsError } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKLIST_QUESTIONS)
      .select("id")
      .eq("production_stage_id", productionStageId)
      .is("deleted_at", null);

    if (questionsError) {
      console.error("Error fetching questions:", questionsError);
      throw questionsError;
    }

    if (!questions || questions.length === 0) {
      return [];
    }

    // Get all PDF references for these questions
    const questionIds = questions.map(q => q.id);
    
    const { data: pdfReferences, error: refsError } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKLIST_PDF_REFERENCES)
      .select("*")
      .in("question_id", questionIds)
      .order("page_number", { ascending: true });

    if (refsError) {
      console.error("Error fetching PDF references:", refsError);
      throw refsError;
    }

    return pdfReferences || [];
  },

  async getPdfReferencesAndDocumentsByProductionStage(productionStageId: string): Promise<{
    pdfReferences: QualityChecklistPdfReference[];
    pdfDocuments: ProjectDocument[];
  }> {
    const supabase = createClient();

    // Get all questions for the production stage
    const { data: questions, error: questionsError } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKLIST_QUESTIONS)
      .select("id")
      .eq("production_stage_id", productionStageId)
      .is("deleted_at", null);

    if (questionsError) {
      console.error("Error fetching questions:", questionsError);
      throw questionsError;
    }

    if (!questions || questions.length === 0) {
      return { pdfReferences: [], pdfDocuments: [] };
    }

    // Get all PDF references for these questions
    const questionIds = questions.map(q => q.id);
    
    const { data: pdfReferences, error: refsError } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKLIST_PDF_REFERENCES)
      .select("*")
      .in("question_id", questionIds)
      .order("page_number", { ascending: true });

    if (refsError) {
      console.error("Error fetching PDF references:", refsError);
      throw refsError;
    }

    if (!pdfReferences || pdfReferences.length === 0) {
      return { pdfReferences: [], pdfDocuments: [] };
    }

    // Get unique document IDs
    const documentIds = [...new Set(pdfReferences.map(ref => ref.document_id))];

    // Get all project documents
    const { data: pdfDocuments, error: docsError } = await supabase
      .from(SUPABASE_TABLES.PROJECT_DOCUMENTS)
      .select("*")
      .in("id", documentIds)
      .is("deleted_at", null)
      .order("name", { ascending: true });

    if (docsError) {
      console.error("Error fetching project documents:", docsError);
      throw docsError;
    }

    return {
      pdfReferences: pdfReferences || [],
      pdfDocuments: pdfDocuments || []
    };
  },

  async getPdfReferencesByDocument(documentId: string): Promise<QualityChecklistPdfReference[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKLIST_PDF_REFERENCES)
      .select("*")
      .eq("document_id", documentId)
      .order("page_number", { ascending: true });

    if (error) {
      console.error("Error fetching PDF references by document:", error);
      throw error;
    }

    return data || [];
  },

  async createPdfReference(reference: QualityChecklistPdfReferenceInsert): Promise<QualityChecklistPdfReference> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKLIST_PDF_REFERENCES)
      .insert(reference)
      .select()
      .single();

    if (error) {
      console.error("Error creating PDF reference:", error);
      throw error;
    }

    return data;
  },

  async updatePdfReference(referenceId: string, updates: QualityChecklistPdfReferenceUpdate): Promise<QualityChecklistPdfReference> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKLIST_PDF_REFERENCES)
      .update(updates)
      .eq("id", referenceId)
      .select()
      .single();

    if (error) {
      console.error("Error updating PDF reference:", error);
      throw error;
    }

    return data;
  },

  async deletePdfReference(referenceId: string): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKLIST_PDF_REFERENCES)
      .delete()
      .eq("id", referenceId);

    if (error) {
      console.error("Error deleting PDF reference:", error);
      throw error;
    }
  },

  // TODO: will revive later
  // Utility function to duplicate checklist questions from one stage to another
  //   async duplicateChecklistItems(
  //     fromProductionStageId: string,
  //     toProductionStageId: string,
  //     projectId: string,
  //     companyId: string,
  //     userId: string
  //   ): Promise<QualityChecklistItem[]> {
  //     const supabase = createClient();

  //     // Get existing questions (latest versions only)
  //     const existingQuestions = await this.getChecklistItems(fromProductionStageId);

  //     if (existingQuestions.length === 0) {
  //       return [];
  //     }

  //     // Create new questions for the target production stage
  //     const newQuestions = existingQuestions.map(question => ({
  //       production_stage_id: toProductionStageId,
  //       project_id: projectId,
  //       company_id: companyId,
  //       created_by: userId,
  //       question_text: question.question_text,
  //       question_uuid: question.question_uuid,
  //       display_order: question.display_order,
  //       is_required: question.is_required,
  //       notes: question.notes,
  //       reference_image_urls: question.reference_image_urls,
  //       min_value: question.min_value,
  //       max_value: question.max_value,
  //       reference_value: question.reference_value,
  //       tolerance_lower: question.tolerance_lower,
  //       tolerance_upper: question.tolerance_upper,
  //       unit: question.unit,
  //       version: 1, // Start with version 1 for duplicated questions
  //     }));

  //     const { data: createdQuestions, error: createError } = await supabase
  //       .from(SUPABASE_TABLES.QUALITY_CHECKLIST_QUESTIONS)
  //       .insert(newQuestions)
  //       .select();

  //     if (createError) {
  //       console.error("Error duplicating checklist questions:", createError);
  //       throw createError;
  //     }

  //     return createdQuestions || [];
  //   },
};